import { useState } from "react";
import { Link } from "react-router";
import { Button } from "../../components/Button";
import { Field, inputShell } from "../../components/Field";
import { login } from "../../api/auth";
import { ApiError } from "../../api/client";
import { BackButton } from "../../components/BackButton";

interface LoginScreenProps {
  /** Set after a successful reset, for the green toast. */
  resetSuccess?: boolean | undefined;
  onSubmit?: ((email: string, password: string) => void) | undefined;
  /** Wrong credentials — one message for both fields, deliberately. */
  credentialsError?: string | null | undefined;
}

/**
 * Screen 20 — email and password.
 *
 * A wrong-credentials error never says which half was wrong: "no account with
 * that email" tells an attacker which emails are registered.
 */
export function LoginScreen({
  resetSuccess = false,
  onSubmit,
  credentialsError,
}: LoginScreenProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [signingIn, setSigningIn] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const emailError = submitted && !emailLooksValid ? "Enter a valid email address." : null;

  const submit = async () => {
    setSubmitted(true);
    if (!emailLooksValid || signingIn) return;

    setSigningIn(true);
    setFailure(null);

    try {
      await login(email.trim().toLowerCase(), password);
      onSubmit?.(email, password);
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : "Couldn't sign you in. Try again.");
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div className="app-column px-5 pb-6 pt-6">
      <BackButton to="/" />

      {resetSuccess ? (
        <p
          role="status"
          className="m-0 rounded-[var(--radius-control)] border border-positive bg-[var(--positive-tint)] px-3.5 py-3 text-[13px] text-positive"
        >
          Password changed — log in with your new one.
        </p>
      ) : null}

      <span className="pt-6 font-[family-name:var(--font-display)] text-[19px] font-bold">
        Prep<span className="text-accent">AI</span>
      </span>
      <h1 className="m-0 pt-4 font-[family-name:var(--font-display)] text-[26px] font-bold leading-[1.15]">
        Welcome back.
      </h1>
      <p className="m-0 pt-1.5 text-[13.5px] text-text-muted">
        Your streak and readiness are waiting.
      </p>

      <div className="flex flex-col gap-4 pt-6">
        <Field label="Email" htmlFor="login-email" error={emailError}>
          <div
            className={`${inputShell} ${(emailError ?? credentialsError ?? failure) ? "border-negative" : "border-border-strong"}`}
          >
            <input
              id="login-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-invalid={Boolean(emailError ?? credentialsError ?? failure)}
              className="w-full bg-transparent text-[15px] text-text outline-none placeholder:text-text-muted"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
        </Field>

        <Field label="Password" htmlFor="login-password" error={credentialsError ?? failure}>
          <div
            className={`${inputShell} ${credentialsError ? "border-negative" : "border-border-strong"}`}
          >
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              autoFocus={resetSuccess}
              aria-invalid={Boolean(credentialsError)}
              aria-describedby={credentialsError ? "login-password-error" : undefined}
              className="w-full bg-transparent text-[15px] text-text outline-none"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
        </Field>

        <Link to="/reset" className="text-[12.5px] font-semibold text-accent">
          Forgot password?
        </Link>
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-8">
        <Button onClick={() => void submit()} disabled={signingIn}>
          {signingIn ? "Signing you in…" : "Log in"}
        </Button>
        <p className="m-0 text-center text-[13px] text-text-muted">
          New here?{" "}
          <Link to="/signup" className="font-semibold text-accent">
            Start practicing free
          </Link>
        </p>
      </div>
    </div>
  );
}
